import { useEffect, useState } from "react";
import {
  ArrowDownload16Regular,
  Dismiss24Regular,
  Document20Regular,
} from "@fluentui/react-icons";
import {
  fetchWorkDiaryAttachmentBlob,
  getWorkDiaryAttachmentDownloadUrl,
  type WorkDiaryAttachmentDto,
} from "../../../workDiary/workDiaryApi";

export const formatAttachmentSize = (bytes?: number | null): string => {
  if (bytes === undefined || bytes === null || !Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const isImageAttachment = (contentType?: string | null): boolean =>
  (contentType ?? "").toLowerCase().startsWith("image/");

// Fetches the PRIVATE attachment blob (authenticated endpoint), turns it into an
// objectURL for previewing, and revokes it on cleanup so we never leak memory.
// The returned objectUrl is shared by every consumer (thumbnail + lightbox), so a
// single fetch/objectURL is reused and revoked exactly once.
function useAttachmentObjectUrl(
  entryId: number,
  attachmentId: number,
  enabled: boolean,
): { objectUrl: string | null; isLoading: boolean; hasError: boolean } {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setObjectUrl(null);
    setHasError(false);
    setIsLoading(true);

    fetchWorkDiaryAttachmentBlob(entryId, attachmentId)
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [entryId, attachmentId, enabled]);

  return { objectUrl, isLoading, hasError };
}

interface DownloadChipProps {
  fileName: string;
  sizeInBytes: number;
  downloadUrl: string;
}

function DownloadChip({ fileName, sizeInBytes, downloadUrl }: DownloadChipProps) {
  return (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 px-3 py-2 bg-accent hover:bg-accent/70 text-sm"
      style={{ borderRadius: "var(--radius)" }}
      title={fileName}
    >
      <Document20Regular className="w-4 h-4 text-primary shrink-0" />
      <span className="text-foreground truncate max-w-[200px]">{fileName}</span>
      <span className="text-secondary-text text-xs whitespace-nowrap">
        {formatAttachmentSize(sizeInBytes)}
      </span>
    </a>
  );
}

interface WorkDiaryAttachmentCardProps {
  entryId: number;
  attachment: WorkDiaryAttachmentDto;
}

// Feed display of one attachment: an inline image thumbnail (click → lightbox) for
// images, or the plain download chip for other files (or when the image fails).
export function WorkDiaryAttachmentCard({
  entryId,
  attachment,
}: WorkDiaryAttachmentCardProps) {
  const isImage = isImageAttachment(attachment.contentType);
  const { objectUrl, hasError } = useAttachmentObjectUrl(
    entryId,
    attachment.id,
    isImage,
  );
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const downloadUrl = getWorkDiaryAttachmentDownloadUrl(entryId, attachment.id);

  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsLightboxOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isLightboxOpen]);

  if (!isImage || hasError) {
    return (
      <DownloadChip
        fileName={attachment.fileName}
        sizeInBytes={attachment.sizeInBytes}
        downloadUrl={downloadUrl}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => objectUrl && setIsLightboxOpen(true)}
        disabled={!objectUrl}
        title={attachment.fileName}
        className="block w-24 h-24 overflow-hidden border border-border bg-muted cursor-pointer"
        style={{ borderRadius: "var(--radius)" }}
      >
        {objectUrl ? (
          <img
            src={objectUrl}
            alt={attachment.fileName}
            className="w-24 h-24 object-cover"
          />
        ) : (
          <span className="block w-24 h-24 bg-muted animate-pulse" />
        )}
      </button>
      <div className="flex items-center gap-1 max-w-[200px]">
        <span
          className="text-foreground text-xs truncate"
          title={attachment.fileName}
        >
          {attachment.fileName}
        </span>
        <span className="text-secondary-text text-xs whitespace-nowrap">
          {formatAttachmentSize(attachment.sizeInBytes)}
        </span>
      </div>
      <a
        href={downloadUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-primary text-xs cursor-pointer"
      >
        <ArrowDownload16Regular className="w-4 h-4 shrink-0" />
        Descarregar
      </a>

      {isLightboxOpen && objectUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={attachment.fileName}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-pointer"
          style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsLightboxOpen(false)}
            aria-label="Fechar"
            className="absolute text-white p-2 cursor-pointer"
            style={{ top: "1rem", right: "1rem" }}
          >
            <Dismiss24Regular className="w-6 h-6" />
          </button>
          <img
            src={objectUrl}
            alt={attachment.fileName}
            className="object-contain"
            style={{ maxWidth: "90vw", maxHeight: "90vh" }}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

interface WorkDiaryAttachmentThumbProps {
  entryId: number;
  attachmentId: number;
  fileName: string;
}

// Compact image preview used inside the edit modal for EXISTING image attachments.
export function WorkDiaryAttachmentThumb({
  entryId,
  attachmentId,
  fileName,
}: WorkDiaryAttachmentThumbProps) {
  const { objectUrl, hasError } = useAttachmentObjectUrl(
    entryId,
    attachmentId,
    true,
  );

  if (hasError) {
    return (
      <span
        className="flex items-center justify-center w-16 h-16 bg-muted border border-border shrink-0"
        style={{ borderRadius: "var(--radius)" }}
      >
        <Document20Regular className="w-5 h-5 text-primary" />
      </span>
    );
  }

  if (!objectUrl) {
    return (
      <span
        className="block w-16 h-16 bg-muted animate-pulse border border-border shrink-0"
        style={{ borderRadius: "var(--radius)" }}
      />
    );
  }

  return (
    <img
      src={objectUrl}
      alt={fileName}
      className="w-16 h-16 object-cover border border-border shrink-0"
      style={{ borderRadius: "var(--radius)" }}
    />
  );
}
